/**
 * Djurslottet - dubbelt draghandtag for prisfiltret.
 *
 * Reglaget ar ett TILLAGG ovanpa Horizons tva sifferfalt, inte en ersattning.
 * Handtagen skriver till samma falt som tidigare och skickar sedan ett
 * change-event, sa temats egen updatePriceFilterAndResults skoter filtreringen.
 * Utan JavaScript fungerar filtret exakt som forut - fyllningen mellan handtagen
 * skrivs ut redan av price-filter.liquid.
 *
 * Custom element i stallet for inline-script: nar facet-sektionen renderas om
 * efter en filtrering satts elementet in pa nytt via innerHTML, och da kors
 * inte inline-script. Ett custom element uppgraderas daremot automatiskt.
 */
class DjPriceSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.djReady === '1') {
      // Redan initierad, men markupen kan ha morphats om under fotterna.
      this.resync();
      return;
    }

    const rangeMax = Number(this.dataset.max) || 0;
    this.step = Number(this.dataset.step) || 1;

    /*
     * Taket avrundas UPP till narmaste steg. Med tak 7399 och steg 25 kan ett
     * range-element bara na 7375, sa handtaget skulle stanna strax fore anden och
     * produkter mellan 7376 och 7399 hade fallit bort. Vi later reglaget ga till
     * 7400 i stallet - allt fran och med det riktiga taket tolkas som "ingen ovre
     * grans" och lamnar sifferfaltet tomt.
     */
    this.ceiling = Math.ceil(rangeMax / this.step) * this.step;

    this.minRange = this.querySelector('[data-dj-range="min"]');
    this.maxRange = this.querySelector('[data-dj-range="max"]');
    this.fill = this.querySelector('[data-dj-fill]');

    const facet = this.closest('price-facet-component');
    if (!facet || !this.minRange || !this.maxRange || !this.ceiling) return;

    this.minField = facet.querySelector('input[name$=".gte"]');
    this.maxField = facet.querySelector('input[name$=".lte"]');
    if (!this.minField || !this.maxField) return;

    this.dataset.djReady = '1';

    this.minRange.max = String(this.ceiling);
    this.maxRange.max = String(this.ceiling);

    this.minRange.addEventListener('input', () => this.#onDrag('min'));
    this.maxRange.addEventListener('input', () => this.#onDrag('max'));
    this.minRange.addEventListener('change', () => this.#commit(this.minField));
    this.maxRange.addEventListener('change', () => this.#commit(this.maxField));

    // Skriver kunden i sifferfalten ska handtagen folja med.
    [this.minField, this.maxField].forEach((field) => {
      field.addEventListener('change', () => {
        this.resync();
        // Temat kan justera vardet till ett giltigt intervall efterat.
        setTimeout(() => this.resync(), 60);
      });
    });

    /*
     * Sidan har TVA prisfilter: ett i den vagrata filterraden pa desktop och ett
     * i filterladan. Filtrerar kunden i det ena stalls det andras falt om av
     * servern, men dess handtag star kvar dar de var. Darfor lyssnar vi bade pa
     * temats filter:update och pa nar elementet faktiskt blir synligt - ladan ar
     * 0x0 tills den oppnas.
     */
    this.onFilterUpdate = () => setTimeout(() => this.resync(), 60);
    document.addEventListener('filter:update', this.onFilterUpdate);

    if (window.IntersectionObserver) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.resync();
        });
      });
      this.visibilityObserver.observe(this);
    }

    this.resync();
  }

  disconnectedCallback() {
    if (this.onFilterUpdate) document.removeEventListener('filter:update', this.onFilterUpdate);
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
  }

  #parse(value) {
    const digits = String(value == null ? '' : value).replace(/[^0-9]/g, '');
    return digits === '' ? null : Number(digits);
  }

  #clamp(value) {
    return Math.min(this.ceiling, Math.max(0, value));
  }

  /** Handtagen far aldrig passera varandra. */
  #onDrag(which) {
    let min = this.#clamp(Number(this.minRange.value));
    let max = this.#clamp(Number(this.maxRange.value));

    if (min > max) {
      if (which === 'min') {
        min = max;
        this.minRange.value = String(min);
      } else {
        max = min;
        this.maxRange.value = String(max);
      }
    }

    // Tomt falt betyder "ingen grans" - det haller URL:en ren.
    this.minField.value = min <= 0 ? '' : String(min);
    this.maxField.value = max >= this.ceiling ? '' : String(max);

    this.#paint(min, max);
  }

  #commit(field) {
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Lasa av sifferfalten och stalla handtag och fyllning efter dem. */
  resync() {
    if (!this.minField || !this.maxField || !this.ceiling) return;
    const min = this.#clamp(this.#parse(this.minField.value) ?? 0);
    const max = this.#clamp(this.#parse(this.maxField.value) ?? this.ceiling);
    this.minRange.value = String(min);
    this.maxRange.value = String(max);
    this.#paint(min, max);
  }

  #paint(min, max) {
    if (!this.fill) return;
    const from = (min / this.ceiling) * 100;
    const to = (max / this.ceiling) * 100;
    this.fill.style.left = from + '%';
    this.fill.style.width = Math.max(0, to - from) + '%';
  }
}

if (!customElements.get('dj-price-slider')) {
  customElements.define('dj-price-slider', DjPriceSlider);
}
